/**
 * §21.7 — clients render countdowns locally from the server's open and close
 * timestamps. No per-second server broadcast is used (§22.5, §31.8.6).
 */

import { useEffect, useMemo, useRef, useState } from "react";

export interface CountdownState {
  /** Whole seconds remaining, floored, never below zero. */
  seconds: number;
  /** Milliseconds remaining. */
  remainingMs: number;
  /** 0..1, for a progress bar. */
  fraction: number;
  expired: boolean;
}

/**
 * A local countdown to an authoritative server timestamp.
 *
 * `clockOffsetMs` corrects for a device clock that disagrees with the server, so
 * the bar reaches zero when the server actually closes the question rather than
 * when the phone thinks it should.
 */
export function useCountdown(
  closesAtMs: number | null,
  totalMs: number,
  clockOffsetMs = 0,
): CountdownState {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (closesAtMs == null) return;
    // Ticked locally, four times a second — smooth enough to read, and it costs
    // the network nothing.
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [closesAtMs]);

  return useMemo(() => {
    if (closesAtMs == null) {
      return { seconds: 0, remainingMs: 0, fraction: 0, expired: true };
    }
    const remainingMs = Math.max(0, closesAtMs - (now + clockOffsetMs));
    return {
      seconds: Math.ceil(remainingMs / 1000),
      remainingMs,
      fraction: totalMs > 0 ? Math.min(1, Math.max(0, remainingMs / totalMs)) : 0,
      expired: remainingMs <= 0,
    };
  }, [closesAtMs, now, totalMs, clockOffsetMs]);
}

/** §8.3 — whether the reading window is still running. */
export function useReadingWindow(opensAtMs: number | null, clockOffsetMs = 0): boolean {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (opensAtMs == null) return;
    const id = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(id);
  }, [opensAtMs]);

  if (opensAtMs == null) return false;
  return now + clockOffsetMs < opensAtMs;
}

/** §12.4 — points animate up from zero rather than appearing at their value. */
export function useCountUp(target: number, durationMs = 900): number {
  const [value, setValue] = useState(0);
  const fromRef = useRef(0);

  useEffect(() => {
    const from = fromRef.current;
    const startedAt = performance.now();
    let frame = 0;

    const step = (timestamp: number) => {
      const progress = Math.min(1, (timestamp - startedAt) / durationMs);
      // Ease-out, so the number decelerates into its final value.
      const eased = 1 - (1 - progress) ** 3;
      setValue(Math.round(from + (target - from) * eased));
      if (progress < 1) frame = requestAnimationFrame(step);
      else fromRef.current = target;
    };

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [target, durationMs]);

  return value;
}

/** §29.2 — the reduced-motion preference, for animations worth skipping. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(query.matches);
    const handler = (event: MediaQueryListEvent) => setReduced(event.matches);
    query.addEventListener("change", handler);
    return () => query.removeEventListener("change", handler);
  }, []);

  return reduced;
}

/** §15.1 — the device-scoped profile token lives in local storage. */
export function useLocalStorage(key: string): [string | null, (value: string | null) => void] {
  const [value, setValue] = useState<string | null>(() => {
    try {
      return localStorage.getItem(key);
    } catch {
      // Private browsing, or storage disabled. The player simply re-onboards.
      return null;
    }
  });

  const update = (next: string | null) => {
    setValue(next);
    try {
      if (next === null) localStorage.removeItem(key);
      else localStorage.setItem(key, next);
    } catch {
      // Non-fatal: the session still works, it just will not resume later.
    }
  };

  return [value, update];
}
